import type { PushEnableResult } from '@/lib/push-client';

/**
 * Map a push enable result to a friendly, jargon-free message for ordinary
 * users. The underlying technical/network error is always kept for developers
 * via console (STEP 11 - log the real backend error, never hide it behind a
 * generic "not configured" or "temporarily unavailable" message).
 *
 * The result carries a stable `code` so the UI can distinguish:
 *  - a device/browser that genuinely cannot do push            -> "Unavailable"
 *  - a real actionable failure where VAPID is unconfigured     -> specific copy
 *  - an auth or network failure                                 -> honest copy
 */
export function friendlyPushError(result: PushEnableResult): string {
  if (!result.ok) {
    const technical = result.error || result.code;
    console.warn('Departmental Labour App notification action failed:', technical);
  }

  if (result.ok) return '';

  switch (result.code) {
    case 'permission-denied':
      return 'Notifications are blocked. Enable them in your browser or device settings to receive alerts.';
    case 'not-configured':
      // Server genuinely has no VAPID keys configured. Only reachable when the
      // server actually answered that push is unconfigured (STEP 14).
      return 'Notifications are temporarily unavailable. Please try again later.';
    case 'not-authorized':
    case 'auth':
      return 'Unable to enable device notifications. Please sign in again and try again.';
    case 'network':
      return 'Unable to enable device notifications. Please check your connection and try again.';
    case 'unsupported':
    case 'push-unavailable':
      return 'Notifications are not supported on this device or browser right now.';
    case 'incomplete':
    case 'unknown':
    default:
      return 'Unable to enable device notifications. Please try again.';
  }
}
